import { parseISO, isValid } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

/*
 * Alle Datums- und Zeitangaben der Anwendung werden in oesterreichischer Zeit
 * gerechnet — unabhaengig davon, wie der Rechner der Nutzer:in eingestellt ist.
 *
 * Warum: Die Datenbank speichert Zeitpunkte (timestamptz) in UTC. Ohne feste
 * Zone rechnet der Browser sie in seine eigene Zeitzone um. Ein Termin, der in
 * Wien am 06.11. um 00:00 beginnt, liegt als 2025-11-05T23:00:00Z im Bestand
 * und erscheint auf einem Rechner mit anderer Zonen-Einstellung als 05.11. oder
 * 06.11. 01:00. Wer den Zeitraum dort erfasst, legt ausserdem eine andere
 * Uhrzeit ab, als er sieht. Die Anwendung wird ausschliesslich in Oesterreich
 * benutzt, also ist die Zone eine Konstante und keine Einstellung.
 *
 * Diese Datei ist die einzige Stelle, an der Zeitpunkte in Wanduhrzeit
 * uebersetzt werden — fuer die Anzeige (format*) wie fuer die Eingabe
 * (toPickerValue/fromPickerValue). Neue Datumsanzeigen bitte ueber diese
 * Helfer, nicht ueber toLocaleDateString oder date-fns/format direkt.
 */
export const TIME_ZONE = 'Europe/Vienna'

// Schreibweise der Zahlen und Monatsnamen. Wie die Zone eine Konstante: Die
// Anwendung ist zweisprachig, das Datumsformat ist es nicht — 05.08.2026 liest
// sich in beiden Sprachfassungen gleich, 8/5/2026 waere in Oesterreich falsch
// herum.
const LOCALE = 'de-AT'

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/*
 * Eingabewerte in einen echten Zeitpunkt uebersetzen. Akzeptiert ISO-Strings,
 * Date und Millisekunden, damit die Aufrufstellen nicht selbst parsen muessen.
 *
 * Sonderfall reines Kalenderdatum ("2026-07-09"): Das ist ein Tag, kein
 * Zeitpunkt. Es wird als Wiener Mitternacht gelesen, damit die Rueckformatierung
 * wieder denselben Tag ergibt. Ohne das haengt das Ergebnis daran, ob der
 * Browser vor oder hinter UTC liegt.
 */
export const toDate = value => {
	if (value === null || value === undefined || value === '') return null
	if (value instanceof Date) return isValid(value) ? value : null

	if (typeof value === 'string') {
		const date = DATE_ONLY.test(value) ? zonedTimeToUtc(`${value} 00:00:00`, TIME_ZONE) : parseISO(value)
		return isValid(date) ? date : null
	}

	const date = new Date(value)
	return isValid(date) ? date : null
}

/*
 * Formatiert einen Zeitpunkt in Wiener Wanduhrzeit. `pattern` ist ein
 * date-fns-Muster. Ungueltige oder leere Werte ergeben einen leeren String,
 * damit Tabellenzellen nicht "Invalid Date" zeigen.
 */
export const formatVienna = (value, pattern) => {
	const date = toDate(value)
	if (!date) return ''
	return formatInTimeZone(date, TIME_ZONE, pattern)
}

/*
 * Die beiden Intl-Varianten fuer die Stellen, die bisher toLocaleDateString
 * bzw. toLocaleString mit 'de-AT' benutzt haben. Sie reichen `options`
 * unveraendert weiter und ergaenzen nur die Zeitzone — die Schreibweise bleibt
 * damit exakt die gewohnte ("5.8.2026", "5. Aug. 2026"), es aendert sich nur,
 * nach welcher Uhr gerechnet wird.
 */
export const formatDate = (value, options) => {
	const date = toDate(value)
	if (!date) return ''
	return date.toLocaleDateString(LOCALE, { timeZone: TIME_ZONE, ...options })
}

export const formatDateTime = (value, options) => {
	const date = toDate(value)
	if (!date) return ''
	return date.toLocaleString(LOCALE, { timeZone: TIME_ZONE, ...options })
}

/**
 * Zeitpunkt in ein Date verschieben, dessen *lokale* Felder die Wiener
 * Wanduhrzeit tragen (getDate(), getHours(), … lesen dann Wiener Werte).
 *
 * Gedacht fuer Fremdcode, der nur die Browser-Zone kennt und deshalb nicht
 * direkt mit Zeitpunkten gefuettert werden kann:
 *
 *   - react-datepicker liest und schreibt die lokalen Felder eines Date;
 *   - Rechnungen mit date-fns (startOfMonth, addMonths, …), die auf
 *     Kalendergrenzen sitzen — eine Monatsgrenze faellt sonst je nach Rechner
 *     in den Vor- oder Folgemonat.
 *
 * Ein solcher Wert ist kein gueltiger Zeitpunkt mehr. Er darf weder gespeichert
 * noch an die Schnittstelle gegeben werden, sondern muss vorher durch
 * fromWallClock zurueck.
 */
export const toWallClock = value => {
	const date = toDate(value)
	if (!date) return null
	return utcToZonedTime(date, TIME_ZONE)
}

/** Gegenstueck zu toWallClock: Wiener Wanduhrzeit wieder als echter Zeitpunkt. */
export const fromWallClock = value => {
	if (!value) return value === undefined ? undefined : null
	const date = value instanceof Date ? value : toDate(value)
	if (!date) return null
	return zonedTimeToUtc(date, TIME_ZONE)
}

/*
 * Tagesgrenzen in Wiener Zeit. date-fns' startOfDay/endOfDay rechnen in der
 * Browser-Zone und wuerden einen Zeitraum je nach Rechner um Stunden versetzt
 * beginnen lassen.
 */
export const startOfDayVienna = value => {
	const date = toDate(value)
	if (!date) return null
	return zonedTimeToUtc(`${formatInTimeZone(date, TIME_ZONE, 'yyyy-MM-dd')} 00:00:00.000`, TIME_ZONE)
}

export const endOfDayVienna = value => {
	const date = toDate(value)
	if (!date) return null
	return zonedTimeToUtc(`${formatInTimeZone(date, TIME_ZONE, 'yyyy-MM-dd')} 23:59:59.999`, TIME_ZONE)
}
